import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { SessionManager, SessionWarningCallbacks, SessionWarningConfig } from "../services/SessionManager";
import Auth from "../common/auth/auth";
import { Utils } from "../common/utils";
import CONFIG from "Config";
import { useToast } from "./useToast";
import { clearPlatformApiSession } from "Services/platform/platform-session";

interface UseSessionManagerProps {
    auth: Auth;
    config?: Partial<SessionWarningConfig>;
    onLogout?: () => void;
    enableToasts?: boolean;
}

interface UseSessionManagerReturn {
    showWarning: boolean;
    minutesRemaining: number;
    isRenewing: boolean;
    continueSession: () => void;
    logout: () => void;
}

export const useSessionManager = ({
    auth,
    config = {},
    onLogout,
    enableToasts = true
}: UseSessionManagerProps): UseSessionManagerReturn => {
    const [showWarning, setShowWarning] = useState(false);
    const [minutesRemaining, setMinutesRemaining] = useState(0);
    const [isRenewing, setIsRenewing] = useState(false);
    const sessionManagerRef = useRef<SessionManager | null>(null);

    // Safely get toast hook, may be undefined if not in toast provider context
    let toast;
    try {
        toast = useToast();
    } catch (error) {
        console.warn("Toast context not available:", error);
        toast = null;
    }

    const defaultLogout = useCallback(() => {
        // SessionProvider does not pass an onLogout, so this is the path the
        // session-expiry modal actually takes. It never reaches the auth
        // provider's logout, so the platform-api cookie has to be dropped here
        // or it outlives the session that created it. Best-effort.
        void clearPlatformApiSession();

        Utils.clearLocalstorage();
        window.location.replace(CONFIG.paths.base + "login");
    }, []);

    const handleLogout = useCallback(() => {
        setShowWarning(false);
        if (onLogout) {
            onLogout();
        } else {
            defaultLogout();
        }
    }, [onLogout, defaultLogout]);

    const continueSession = useCallback(async () => {
        if (!sessionManagerRef.current) return;

        setIsRenewing(true);
        try {
            const success = await sessionManagerRef.current.renewSession();
            if (success) {
                setShowWarning(false);
                if (enableToasts && toast) {
                    toast.toast({
                        title: "Session Extended",
                        message: "Your session has been successfully renewed.",
                        type: "success"
                    });
                }
            }
        } catch (error) {
            console.error("Failed to renew session:", error);
            if (enableToasts && toast) {
                toast.toast({
                    title: "Session Renewal Failed",
                    message: "Unable to extend your session. You will be logged out.",
                    type: "error"
                });
            }
            handleLogout();
        } finally {
            setIsRenewing(false);
        }
    }, [handleLogout, toast, enableToasts]);

    const callbacks: SessionWarningCallbacks = useMemo(() => ({
        onWarning: (minutes: number) => {
            setMinutesRemaining(minutes);
            setShowWarning(true);
        },
        onExpired: () => {
            setShowWarning(false);
            handleLogout();
        },
        onRenewed: () => {
            setShowWarning(false);
        }
    }), [handleLogout]);

    const sessionConfig = useMemo(() => ({
        warningThresholdMinutes: 5,
        checkIntervalSeconds: 30,
        ...config
    }), [config?.warningThresholdMinutes, config?.checkIntervalSeconds]);

    useEffect(() => {
        // Only start session monitoring if user is authenticated
        if (!auth.isAuthenticated()) {
            return;
        }

        sessionManagerRef.current = new SessionManager(sessionConfig, callbacks, auth);
        sessionManagerRef.current.startMonitoring();

        return () => {
            if (sessionManagerRef.current) {
                sessionManagerRef.current.stopMonitoring();
            }
        }

    }, [auth, sessionConfig, callbacks]);

    // Clean up when component unmounts
    useEffect(() => {
        return () => {
            if (sessionManagerRef.current) {
                sessionManagerRef.current.stopMonitoring();
            }
        };
    }, []);

    return {
        showWarning,
        minutesRemaining,
        isRenewing,
        continueSession,
        logout: handleLogout
    };
};